import { ChainID } from '@jdiyield/constants'
import { Contract, PriceList, Stake, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import throat from 'throat'
import Web3 from 'web3'

import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { StakeFunc, StakeProps } from '../types'
import { findToken, makeBatchRequest, safeGasEstimates } from '../utils'

export const getMiniChefFarm = async (
  walletAddress: string,
  ct: Contract,
  tokens: Token[],
  priceList: PriceList,
  web3Prop: Web3
): Promise<Stake[]> => {
  try {
    const { contractAddress, rewardAddress, routerAddress, pendingReward, showReward, functionRewardPerBlockName } = ct
    const { miniChefGenABI } = await import('@jdiyield/abi')
    const web3 = web3Prop
    const contract = new web3.eth.Contract(
      miniChefGenABI({ rewardPerBlock: functionRewardPerBlockName, pendingReward }) as any,
      contractAddress
    )

    const _poolLength = await contract.methods
      .poolLength()
      .call()
      .catch(() => 1)
    const poolArray = [...Array(parseInt(_poolLength))]

    let _userInfoResults = []

    _userInfoResults = await makeBatchRequest(
      web3,
      poolArray.map((_data, poolId) => {
        return contract.methods.userInfo(poolId, walletAddress).call
      })
    )

    const _balances = _userInfoResults.map((result, poolId) => {
      let balance = '0' as any
      balance = result['amount']
      return { pool: poolId, balance: balance || '0' }
    })
    const poolWhiteList = _balances.filter(({ balance }) => +balance > 0).map(({ pool }) => pool)
    const filteredBalance = _balances.filter(({ pool }) => poolWhiteList.includes(pool))
    const _balancesLP = await makeBatchRequest(
      web3,
      filteredBalance.map((b) => contract.methods.lpToken(b.pool).call)
    )
    const _balancesLPResult = _balancesLP.map((lpAddress: any, i: string | number) => ({
      ...filteredBalance[i],
      lpAddress: `${lpAddress}`
    }))
    const _balanceLPPair = _balancesLPResult.map(
      throat(80, async (lp: any) => {
        return calculate(
          web3,
          {
            ...lp
          },
          routerAddress,
          tokens,
          priceList
        )
      })
    )

    const _pendingReward = async () => {
      if (!showReward) return _balancesLPResult.map((lp: any) => ({ ...lp, reward: 0 }))
      try {
        const result = await makeBatchRequest(
          web3,
          _balancesLPResult
            .filter(({ pool }) => poolWhiteList.includes(pool))
            .map((lp: { pool: any }) => {
              console.log(lp, pendingReward)
              return contract.methods[`${pendingReward}`](lp.pool, walletAddress).call
            })
        )
        return result.map((reward: any, index: string | number) => ({ ..._balancesLPResult[index], reward }))
      } catch (error) {
        console.log(_balancesLPResult, error)
        return _balancesLPResult.map((lp: any) => ({ ...lp, reward: 0 }))
      }
    }

    const [completeBalance, completePendingReward] = await Promise.all([
      Promise.all(_balanceLPPair),
      // Promise.all(_balanceYourLNotStake),
      _pendingReward()
    ])

    const rewardSymbol = findToken(tokens, rewardAddress)?.symbol ?? '???'
    const result = completeBalance
      .filter((obj) => obj !== null)
      .map((i) => {
        // const availableBalanceOf =
        //   (completeBalanceNotStake.find((a: any) => a.pool === i.pool)?.availableBalanceOf / 1e18).toString() || '0'
        const reward = +completePendingReward?.find((a: any) => a.pool === i.pool)?.reward / 1e18 || 0
        return {
          ...i,
          ...ct,
          availableBalanceOf: '0',
          reward,
          rewardSymbol
        } as Stake
      })
    return result
  } catch (e) {
    console.log(e)
    return []
  }
}

export const stakeMiniChef: StakeFunc = async ({ web3, pool, contractAddress, walletAddress, amount }) => {
  try {
    const { miniChefGenABI } = await import('@jdiyield/abi')
    const MiniChef = miniChefGenABI({}) as any
    const ct = new web3.eth.Contract(MiniChef as any, contractAddress)
    const contractWithMethod = ct.methods.deposit(
      pool,
      new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed(),
      walletAddress
    )
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const unStakeMiniChef: StakeFunc = async ({ web3, pool, contractAddress, walletAddress, amount }) => {
  try {
    const { miniChefGenABI } = await import('@jdiyield/abi')
    const MiniChef = miniChefGenABI({}) as any
    const ct = new web3.eth.Contract(MiniChef as any, contractAddress)
    const contractWithMethod = ct.methods.withdrawAndHarvest(
      pool,
      new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed(),
      walletAddress
    )
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const harvestMiniChef: StakeFunc<Omit<StakeProps, 'amount'>> = async ({
  web3,
  pool,
  contractAddress,
  walletAddress
}) => {
  try {
    const { miniChefGenABI } = await import('@jdiyield/abi')
    const MiniChef = miniChefGenABI({}) as any
    const ct = new web3.eth.Contract(MiniChef as any, contractAddress)
    const contractWithMethod = ct.methods.harvest(pool, walletAddress)
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const getMiniChefAPR = async (
  contractAddress: string,
  functionRewardPerBlockName = 'sushiPerSecond',
  poolId: number,
  chainID: ChainID
): Promise<{
  rewardPerBlock: string
  multiplier: string
  allocPoints: string
  totalAllocPoints: string
}> => {
  try {
    const RPC_URL = getNodeUrl(chainID)
    const web3 = await new Web3(RPC_URL)
    const { miniChefGenABI } = await import('@jdiyield/abi')
    const miniChef = miniChefGenABI({ rewardPerBlock: functionRewardPerBlockName }) as any
    const ct = new web3.eth.Contract(miniChef, contractAddress)
    const [poolInfo, rewardPerBlock] = await makeBatchRequest(web3, [
      ct.methods.poolInfo(poolId).call,
      ct.methods[`${functionRewardPerBlockName}`]().call
    ])

    const allocPoints = poolInfo[2]
    const totalAllocPoints =
      (await ct.methods
        .totalAllocPoint()
        .call()
        .catch(() => allocPoints)) || +allocPoints

    return {
      rewardPerBlock,
      multiplier: '10',
      allocPoints,
      totalAllocPoints
    }
  } catch (error) {
    console.log('getSuShiAPR', contractAddress, error)
  }
}
